import React, { forwardRef } from "react";
import { NavLink, Link } from "react-router-dom";
import { ReactSVG } from "react-svg";
import { Oval } from "react-loader-spinner";
import { setSVGColor } from "../SVGHelper";
import "../style/buttons.scss";

/**
 * Button/Link component with optional SVG icon and positions/sizes them according to the brand guide.
 *
 * @note If you pass a className prop, you will lose the default button styling, so please use the classes from buttons.scss
 *
 * @param {Function} [props.onClick] Callback, runs before 'to'.
 * @param {string} [props.svg] SVG path for the icon, color is set based on text color and size is set based brand guide/size class.
 * @param {boolean} [props.iconAfter=false] The icon will be displayed after the button text instead of before.
 * @param {string} [props.to] If provided, the button acts as a link with the given path.
 * @param {boolean} [props.disabled=false] Determines if the button interaction is disabled and sets styling accordingly.
 * @param {boolean} [props.round=false] If true, the button will have a rounded shape, a complete circle if no text is passed.
 * @param {string} [props.indicateActiveNav=false] Use NavLink(if possible) + .active styling for active nav links.
 * @param {string} [props.className='btn-primary'] CSS class names to be added to the button.
 * @param {boolean} [props.loading=false] If true, the button will display a loading spinner instead of its children.
 * @param {Object} [props.rest={}] Any other props will be passed to the button.
 *
 * @returns {ReactElement} The rendered Button component.
 *
 * @example <Button svg={icon} className="btn-primary-destructive btn-sm" round iconAfter>click me</Button>
 */
const Button = forwardRef(
    (
        {
            onClick,
            svg,
            iconAfter = false,
            to,
            disabled,
            children,
            style = {},
            round,
            indicateActiveNav = false,
            className = "btn-primary",
            loading = false,
            ...rest
        },
        ref,
    ) => {
        const spaceBetweenIconAndText = "8px";

        const iconOnlyPadding = {
            "btn-xs": { padding: "9px" },
            "btn-sm": { padding: "11px" },
            "btn-md": { padding: "15px" },
            "btn-lg": { padding: "18px" },
            "btn-xl": { padding: "18px" },
            "btn-xxl": { padding: "20px" },
        };

        const iconSizes = {
            "btn-xs": "18px",
            "btn-sm": "18px",
            "btn-md": "18px",
            "btn-lg": "20px",
            "btn-xl": "24px",
            "btn-xxl": "24px",
        };

        const classNames = className.split(" ");
        const sizingClass = classNames.find((cn) => iconOnlyPadding[cn]) || Object.keys(iconOnlyPadding)[0];
        const padding = !children ? iconOnlyPadding[sizingClass] : {};
        const iconSize = iconSizes[sizingClass];

        const finalStyle = {
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            ...(round ? { borderRadius: "999999px" } : {}), // Apply borderRadius only if round is true
            ...padding,
            ...style,
        };

        const defaultSvgStyle = { pointerEvents: "none" };
        const icon = svg ? (
            <ReactSVG
                src={svg}
                beforeInjection={(svg) => {
                    svg.setAttribute("width", iconSize);
                    svg.setAttribute("height", iconSize);
                    setSVGColor("currentColor")(svg);
                }}
                style={
                    children
                        ? {
                              marginLeft: iconAfter ? spaceBetweenIconAndText : undefined,
                              marginRight: !iconAfter ? spaceBetweenIconAndText : undefined,
                              ...defaultSvgStyle,
                          }
                        : defaultSvgStyle
                }
            />
        ) : null;

        const LinkType = { NONE: 0, RELATIVE: 1, ABSOLUTE: 2 };

        let linkType = to
            ? ["/", "#"].some((c) => to.startsWith(c))
                ? LinkType.RELATIVE
                : LinkType.ABSOLUTE
            : LinkType.NONE;

        if (linkType === LinkType.ABSOLUTE) {
            // can we make it relative? (works with react-router-dom for smoother transition/avoiding page reloads)
            const url = new URL(to);
            if (url.host === window.location.host && url.port === window.location.port) {
                //yes, make it relative
                to = url.pathname + url.search + url.hash;
                linkType = LinkType.RELATIVE;
            }
        }

        const Component = {
            [LinkType.RELATIVE]: indicateActiveNav ? NavLink : Link,
            [LinkType.ABSOLUTE]: "a",
            [LinkType.NONE]: "button",
        }[linkType];

        return (
            <Component
                to={to}
                href={to}
                disabled={disabled || loading}
                onClick={onClick}
                style={finalStyle}
                className={`${className} ${loading ? "loading" : ""} ${disabled ? "disabled" : ""}`}
                ref={ref}
                {...rest}
            >
                {!iconAfter && icon}
                <div style={{ position: "relative" }}>
                    {loading && (
                        <Oval
                            wrapperStyle={{
                                position: "absolute",
                                width: "100%",
                                height: "100%",
                            }}
                            width="100%"
                            height="100%"
                            color="white"
                            secondaryColor="white"
                            strokeWidth={4}
                        />
                    )}
                    <span style={{ visibility: loading ? "hidden" : "visible" }}>{children}</span>
                </div>
                {iconAfter && icon}
            </Component>
        );
    },
);

export default Button;
